import type { ComponentProps } from "react";
import { forwardRef } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Input } from "../input/Input";
import { css } from "@emotion/css";
import { ja } from "date-fns/locale";
import type { Locale } from "date-fns";

export interface DatepickerProps {
    selected: Date | null;
    onChange: (date: Date | null) => void;
    placeholderText?: string;
    isClearable?: boolean;
    zIndex?: number;
    width?: string;
    includeDates?: Date[];
    locale?: string;
    dateFormat?: string;
    customInput?: ComponentProps<typeof DatePicker>["customInput"];
}

const locales: Record<string, Locale> = {
    "ja-JP": ja,
};
Object.entries(locales).forEach(([key, value]) => registerLocale(key, value));

export const Datepicker = forwardRef<HTMLInputElement, DatepickerProps>(
    (
        {
            selected,
            onChange,
            placeholderText,
            isClearable,
            zIndex = 1,
            width = "100%",
            includeDates,
            locale,
            dateFormat,
            customInput,
        },
        ref
    ) => {
        const inputField = customInput ?? <Input ref={ref} autoComplete="off" />;
        const localeValue = locale && locales?.[locale] ? locale : undefined;
        return (
            <DatePicker
                placeholderText={placeholderText}
                isClearable={isClearable}
                customInput={inputField}
                portalId="datepicker-popper"
                wrapperClassName={css(`
                    width: ${width};
                `)}
                popperClassName={css(`
                    z-index: ${zIndex} !important;
                `)}
                selected={selected}
                onChange={onChange}
                includeDates={includeDates}
                locale={localeValue}
                autoComplete="off"
                dateFormat={dateFormat}
            />
        );
    }
);

Datepicker.displayName = "Datepicker";
